// The Mail workspace's vocabulary on the office client (Session AUB, email system Phase 4). A MIRROR of
// server/src/modules/mail/lanes.ts: the lane, state and dismissal labels below must stay byte for byte the server's, which
// server/src/modules/mail/mailVocabularyParity.test.ts holds. Change both in the same edit, never one.
//
// Also the workspace's keyboard map. A shortcut is always an ADDITION: every action it reaches has a labelled button too
// (Item 8: no keyboard shortcut is the only way to do anything).
// No import from ./api on purpose: the server parity test imports this file, and the server typecheck must not pull the
// client API module in with it. The three key types below are the same unions src/lib/api.ts declares.
export type MailLane = "reply" | "person" | "work" | "filed" | "all";
export type MailState = "filed" | "work" | "pending" | "superseded" | "answered" | "dismissed" | "snoozed" | "reply" | "person" | "no_reply";
export type MailDismissReason = "no_action_needed" | "handled_outside_email" | "not_for_ele" | "spam_or_marketing" | "duplicate" | "other";

export const MAIL_LANES = ["reply", "person", "work", "filed", "all"] as const;

export const MAIL_LANE_LABELS: Record<MailLane, string> = {
  reply: "Reply",
  person: "Person",
  work: "Work orders",
  filed: "Filed",
  all: "All",
};

export const MAIL_STATE_LABELS: Record<MailState, string> = {
  filed: "Filed",
  work: "Work order",
  pending: "Being decided",
  superseded: "Newer message",
  answered: "Answered",
  dismissed: "Dismissed",
  snoozed: "Snoozed",
  reply: "Reply ready",
  person: "Needs a person",
  no_reply: "No reply needed",
};

export const DISMISS_REASONS = ["no_action_needed", "handled_outside_email", "not_for_ele", "spam_or_marketing", "duplicate", "other"] as const;

export const DISMISS_REASON_LABELS: Record<MailDismissReason, string> = {
  no_action_needed: "No action needed",
  handled_outside_email: "Handled outside email (phone, in person or in Gmail)",
  not_for_ele: "Not for East London Elevators",
  spam_or_marketing: "Spam or marketing",
  duplicate: "A duplicate of another conversation",
  other: "Something else (say what)",
};

/** Mirrors the server's DISMISS_NOTE_MAX. */
export const DISMISS_NOTE_MAX = 500;

/** A refusal in plain words, or null. Mirrors the server's dismissalRefusal, so the office is told before the request. */
export function dismissalRefusal(reason: MailDismissReason, note: string): string | null {
  const words = note.trim();
  if (reason === "other" && words.length === 0) return 'Say why it is being dismissed: the reason "Something else" needs a few words.';
  if (words.length > DISMISS_NOTE_MAX) return `Keep the note to ${DISMISS_NOTE_MAX} characters.`;
  return null;
}

// ---- The keyboard map (laptop) ------------------------------------------------------------------------------------------
export type MailShortcutAction =
  | "next"
  | "previous"
  | "open"
  | "send"
  | "edit"
  | "skip"
  | "assignFirst"
  | "assignSecond"
  | "assignMenu"
  | "snoozeMenu"
  | "dismiss"
  | "gmail"
  | "search"
  | "help";

/** Each key the workspace answers, and what it does. ArrowDown/ArrowUp mirror j/k. */
export const MAIL_SHORTCUTS: Record<string, MailShortcutAction> = {
  j: "next",
  ArrowDown: "next",
  k: "previous",
  ArrowUp: "previous",
  Enter: "open",
  o: "open",
  s: "send",
  e: "edit",
  x: "skip",
  "1": "assignFirst",
  "2": "assignSecond",
  a: "assignMenu",
  z: "snoozeMenu",
  d: "dismiss",
  g: "gmail",
  "/": "search",
  "?": "help",
};

/** The help list, in the order the office reads it. */
export const MAIL_SHORTCUT_HELP: Array<{ keys: string; does: string }> = [
  { keys: "j or Down arrow", does: "Next conversation" },
  { keys: "k or Up arrow", does: "Previous conversation" },
  { keys: "Enter or o", does: "Open the selected conversation" },
  { keys: "s", does: "Approve and send (opens the confirm first; nothing is sent until you press Send there)" },
  { keys: "e", does: "Edit the reply" },
  { keys: "x", does: "Skip to the next conversation" },
  { keys: "1 / 2", does: "Assign to the first / second person in the assign list" },
  { keys: "a", does: "Choose who to assign it to" },
  { keys: "z", does: "Snooze" },
  { keys: "d", does: "Dismiss with a reason" },
  { keys: "g", does: "Open the conversation in Gmail" },
  { keys: "/", does: "Search" },
  { keys: "?", does: "Show this list" },
];

/**
 * The shortcut a key press means, or null when the workspace must ignore it: a modifier is held, the person is typing in a
 * field, or a dialog is open. Pure apart from reading the event's target.
 */
// Typed without the DOM lib on purpose: the server parity test imports this file under the server tsconfig.
export interface ShortcutKeyEvent {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  target: unknown;
}

export function shortcutFor(e: ShortcutKeyEvent, dialogOpen: boolean): MailShortcutAction | null {
  if (dialogOpen) return null;
  if (e.ctrlKey || e.metaKey || e.altKey) return null;
  const t = e.target as { closest?: (selector: string) => unknown } | null;
  if (t && typeof t.closest === "function") {
    if (t.closest("input, textarea, select, [contenteditable='true'], [role='dialog']")) return null;
  }
  return MAIL_SHORTCUTS[e.key] ?? null;
}

// ---- Snooze presets (London time) -------------------------------------------------------------------------------------

/** The London wall-clock offset, in minutes, at an instant (60 in summer, 0 in winter). */
function londonOffsetMinutes(at: Date): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(at);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? "0");
  const asUtc = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second"));
  return Math.round((asUtc - at.getTime()) / 60_000);
}

/** The London calendar day of an instant, as numbers. */
function londonDay(at: Date): { y: number; m: number; d: number; weekday: number } {
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", year: "numeric", month: "2-digit", day: "2-digit", weekday: "short" }).formatToParts(at);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return { y: Number(get("year")), m: Number(get("month")), d: Number(get("day")), weekday: Math.max(0, weekdays.indexOf(get("weekday"))) };
}

/** A London wall-clock time as an ISO string WITH its offset, e.g. 2026-10-03T08:00:00+01:00. */
export function londonIso(y: number, m: number, d: number, hour: number, minute = 0): string {
  // First guess the instant as if London were UTC, then correct by the offset in force at that instant.
  const guess = new Date(Date.UTC(y, m - 1, d, hour, minute));
  const offset = londonOffsetMinutes(new Date(guess.getTime() - londonOffsetMinutes(guess) * 60_000));
  const pad = (n: number) => String(n).padStart(2, "0");
  const sign = offset >= 0 ? "+" : "-";
  const abs = Math.abs(offset);
  return `${y}-${pad(m)}-${pad(d)}T${pad(hour)}:${pad(minute)}:00${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}

function addDays(y: number, m: number, d: number, n: number): { y: number; m: number; d: number } {
  const t = new Date(Date.UTC(y, m - 1, d + n, 12));
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
}

export type SnoozePreset = "tomorrow" | "monday" | "threeDays" | "week";
export const SNOOZE_PRESET_LABELS: Record<SnoozePreset, string> = {
  tomorrow: "Until tomorrow 8am",
  monday: "Until Monday 8am",
  threeDays: "In 3 days",
  week: "In a week",
};

/** The instant a preset means, as an ISO string with the London offset. Pure given `now`. */
export function snoozeUntil(preset: SnoozePreset, now: Date): string {
  const today = londonDay(now);
  if (preset === "tomorrow") {
    const t = addDays(today.y, today.m, today.d, 1);
    return londonIso(t.y, t.m, t.d, 8);
  }
  if (preset === "monday") {
    // The NEXT Monday (a Monday morning snoozes to the following week's Monday).
    const ahead = ((8 - today.weekday) % 7) || 7;
    const t = addDays(today.y, today.m, today.d, ahead);
    return londonIso(t.y, t.m, t.d, 8);
  }
  const days = preset === "threeDays" ? 3 : 7;
  const hm = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(now);
  const hour = Number(hm.find((p) => p.type === "hour")?.value ?? "8");
  const minute = Number(hm.find((p) => p.type === "minute")?.value ?? "0");
  const t = addDays(today.y, today.m, today.d, days);
  return londonIso(t.y, t.m, t.d, hour, minute);
}

/** A <input type="datetime-local"> value (London wall clock, no zone) as an ISO string with the London offset. */
export function londonLocalInputToIso(value: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!m) return null;
  return londonIso(Number(m[1]), Number(m[2]), Number(m[3]), Number(m[4]), Number(m[5]));
}

/** A stored instant in London time, en-GB, for the office. */
export function fmtLondonDateTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB", { timeZone: "Europe/London", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
